import { Box } from "@mui/material";

import { GetStaticPropsContext } from "next";
import { useTranslation } from "next-i18next";

import { useRouter } from "next/router";

import React, { useContext, useEffect } from "react";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Configure, InstantSearch, PoweredBy } from "react-instantsearch-hooks-web";

import * as ROUTES from "./_routes.json";

import Layout from "../components/Layout";
import ContentContainer from "../components/Molecules/ContentContainer";
import PageTitle from "../components/Molecules/PageTitle";
import SEO from "../components/Molecules/SEO";
import AlternateLangContext from "../context/alternateLangContext/context";
import useAlternateLinkTo from "../hooks/useAlternateLinkTo";

// eslint-disable-next-line import/order
import { Search, useSearchClient } from "@/features/search";

const SearchPage = () => {
    const { locale } = useRouter();
    const { t } = useTranslation("search");
    const alternateLinks = useAlternateLinkTo("search", ROUTES);
    const { setAlternateUrls } = useContext(AlternateLangContext);

    useEffect(() => {
        setAlternateUrls(alternateLinks);
    }, [alternateLinks, setAlternateUrls]);

    const title = t("title");
    const metaTitle = t("metaTitle");
    const metaDescription = t("metaDescription");

    const canonical = alternateLinks.find((link) => link.hrefLang === locale)?.href;

    const searchLocale = locale || "en";

    const otherLocale = searchLocale === "en" ? "fr" : "en";

    const searchClient = useSearchClient();

    // return all hits, group by type in UI
    // ? filter by locale where 2 matches exist and only pick the one that matches the locale

    return (
        <>
            <SEO
                title={metaTitle}
                description={metaDescription}
                openGraph={{
                    title: metaTitle,
                    description: metaDescription,
                    type: "webpage"
                }}
                // Remove current language from alternate links
                alternateLinks={alternateLinks}
                canonical={`https://carl-topham.com${canonical}`}
            />

            <PageTitle title={title} />

            {searchClient ? (
                <ContentContainer component="section">
                    <InstantSearch
                        searchClient={searchClient}
                        indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME}
                    >
                        <Search.InputBox />
                        <Search.PopularQueries
                            queries={[
                                "React",
                                "JavaScript",
                                "TypeScript",
                                "Next.js",
                                "CSS",
                                "Sass"
                            ]}
                        />

                        <Search.EmptyQueryBoundary fallback={null}>
                            <Search.NoResultsBoundary fallback={<Search.NoResults />}>
                                <>
                                    <Search.RefinementList
                                        attribute="type"
                                        transformItems={(items) =>
                                            items.map((item) => ({
                                                ...item,
                                                label: item.label.toUpperCase()
                                            }))
                                        }
                                    />
                                    <Configure
                                        hitsPerPage={50}
                                        ruleContexts={[searchLocale]}
                                        // Rank by locale and type so that the locale matches are ranked higher
                                        // and types of "tag" are ranked higher than "article"
                                        filters={`(locale:${searchLocale}<score=5> OR locale:${otherLocale}<score=1>) AND (type:tag<score=99> OR type:tip<score=20> OR type:article<score=2> OR type:project<score=2> OR type:experience<score=2>  OR type:run<score=1>)`}
                                        facetFilters={
                                            // filter out french results if locale is english
                                            searchLocale === "en"
                                                ? [`locale:${searchLocale}`]
                                                : []
                                        }
                                        queryLanguages={[searchLocale]}
                                    />
                                    <Search.HitContainer />
                                </>
                            </Search.NoResultsBoundary>
                        </Search.EmptyQueryBoundary>
                        <Box sx={{ width: "150px", mt: 2, mx: "auto" }}>
                            <PoweredBy />
                        </Box>
                    </InstantSearch>
                </ContentContainer>
            ) : null}
        </>
    );
};

SearchPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

export async function getStaticProps(context: GetStaticPropsContext) {
    const { locale } = context;
    return {
        props: {
            ...(await serverSideTranslations(locale || "en", ["common", "search", "nav"]))
        }
    };
}

export default SearchPage;
